import type { ApiKeyResponseDto } from '@/apis/backend/generated/models';

import { API_URL } from '@/lib/api';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useOrganization } from '@/context/organization-context';
import { CreateApiKeyDtoScopesItem } from '@/apis/backend/generated/models/createApiKeyDtoScopesItem';
import {
  useMcpApiKeysControllerListV1,
  useMcpApiKeysControllerCreateV1,
  useMcpApiKeysControllerRevokeV1,
} from '@/apis/backend/generated/api-keys/api-keys';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CardContent from '@mui/material/CardContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormHelperText from '@mui/material/FormHelperText';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Add as AddIcon,
  Api as ApiIcon,
  Send as SendIcon,
  VpnKey as KeyIcon,
  Image as ImageIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  MailOutline as MailIcon,
  Terminal as TerminalIcon,
  OpenInNew as OpenInNewIcon,
  Inventory2 as InventoryIcon,
  Storefront as StorefrontIcon,
  DesignServices as DesignServicesIcon,
} from '@mui/icons-material';

import { WebhooksSection } from 'src/components/settings/WebhooksSection';
import { OrgSectionPicker } from 'src/components/settings/OrgSectionPicker';

type ApiKeyScope = (typeof CreateApiKeyDtoScopesItem)[keyof typeof CreateApiKeyDtoScopesItem];

type TFunction = (key: string, defaultValue: string) => string;

type McpClientId = 'claude-code' | 'claude-desktop' | 'codex' | 'chatgpt';

type ScopeChoice = { value: ApiKeyScope; label: string; danger?: boolean };

type ScopeGroup = {
  key: string;
  title: string;
  description: string;
  icon: React.ReactElement;
  choices: ScopeChoice[];
};

// Grouped by resource rather than listed flat: a key normally needs read+write
// on one or two resources, and thirteen undifferentiated checkboxes make the
// least-privilege choice harder than granting everything.
//
// The `api` wildcard is deliberately not offered here. It exists so keys issued
// before granular scopes keep working; steering new keys to it would undo the
// point of having scopes at all.
//
// `mcp` is deliberately not a group either. These scopes gate the public REST
// API resource by resource; `mcp` gates a separate surface where an agent acts
// with the org's own authority and every tool is reachable. Listing it as a
// sibling checkbox reads as "MCP is another resource permission" and invites the
// conclusion that the REST scopes constrain what a connected agent can do. They
// do not, so it gets its own section in the dialog.
function getScopeGroups(t: TFunction): ReadonlyArray<ScopeGroup> {
  return [
    {
      key: 'brands',
      title: t('apiKeys.scopes.brands.title', 'Brands'),
      description: t('apiKeys.scopes.brands.description', 'Create brands, read and update brand profiles'),
      icon: <StorefrontIcon sx={{ fontSize: 18 }} />,
      choices: [
        { value: CreateApiKeyDtoScopesItem['brands:read'], label: t('apiKeys.scopes.read', 'Read') },
        { value: CreateApiKeyDtoScopesItem['brands:write'], label: t('apiKeys.scopes.write', 'Write') },
      ],
    },
    {
      key: 'templates',
      title: t('apiKeys.scopes.templates.title', 'Templates & emails'),
      description: t(
        'apiKeys.scopes.templates.description',
        'Read templates, run pre-send checks, and generate emails from a brief',
      ),
      icon: <MailIcon sx={{ fontSize: 18 }} />,
      choices: [
        { value: CreateApiKeyDtoScopesItem['templates:read'], label: t('apiKeys.scopes.read', 'Read') },
        { value: CreateApiKeyDtoScopesItem['templates:write'], label: t('apiKeys.scopes.write', 'Write') },
      ],
    },
    {
      key: 'products',
      title: t('apiKeys.scopes.products.title', 'Products'),
      description: t('apiKeys.scopes.products.description', 'Read the catalog, upsert products in bulk'),
      icon: <InventoryIcon sx={{ fontSize: 18 }} />,
      choices: [
        { value: CreateApiKeyDtoScopesItem['products:read'], label: t('apiKeys.scopes.read', 'Read') },
        { value: CreateApiKeyDtoScopesItem['products:write'], label: t('apiKeys.scopes.write', 'Write') },
      ],
    },
    {
      key: 'media',
      title: t('apiKeys.scopes.media.title', 'Media'),
      description: t('apiKeys.scopes.media.description', 'Read the media library, import assets by URL'),
      icon: <ImageIcon sx={{ fontSize: 18 }} />,
      choices: [
        { value: CreateApiKeyDtoScopesItem['media:read'], label: t('apiKeys.scopes.read', 'Read') },
        { value: CreateApiKeyDtoScopesItem['media:write'], label: t('apiKeys.scopes.write', 'Write') },
      ],
    },
    {
      key: 'esp',
      title: t('apiKeys.scopes.esp.title', 'Email platform'),
      description: t(
        'apiKeys.scopes.esp.description',
        'Read connected ESPs, export templates, create campaigns',
      ),
      icon: <SendIcon sx={{ fontSize: 18 }} />,
      choices: [
        { value: CreateApiKeyDtoScopesItem['esp:read'], label: t('apiKeys.scopes.read', 'Read') },
        { value: CreateApiKeyDtoScopesItem['esp:export'], label: t('apiKeys.scopes.export', 'Export') },
        {
          value: CreateApiKeyDtoScopesItem['esp:send'],
          label: t('apiKeys.scopes.send', 'Send'),
          danger: true,
        },
      ],
    },
  ];
}

const SCOPE_META: Record<string, { label: string; icon?: React.ReactElement; tone: 'primary' | 'default' | 'warning' }> = {
  mcp: { label: 'MCP', icon: <TerminalIcon sx={{ fontSize: 14 }} />, tone: 'primary' },
  api: { label: 'API (all)', icon: <ApiIcon sx={{ fontSize: 14 }} />, tone: 'default' },
  figma: { label: 'FIGMA', icon: <DesignServicesIcon sx={{ fontSize: 14 }} />, tone: 'default' },
  'brands:read': { label: 'Brands: read', tone: 'default' },
  'brands:write': { label: 'Brands: write', tone: 'default' },
  'templates:read': { label: 'Templates: read', tone: 'default' },
  'templates:write': { label: 'Templates: write', tone: 'default' },
  'products:read': { label: 'Products: read', tone: 'default' },
  'products:write': { label: 'Products: write', tone: 'default' },
  'media:read': { label: 'Media: read', tone: 'default' },
  'media:write': { label: 'Media: write', tone: 'default' },
  'esp:read': { label: 'ESP: read', tone: 'default' },
  'esp:export': { label: 'ESP: export', tone: 'default' },
  'esp:send': { label: 'ESP: send', tone: 'warning' },
};

export const Route = createFileRoute(
  '/_authed/dashboard/o/$orgId/settings/organization/api',
)({
  component: ApiKeysPage,
});

function ApiKeysPage() {
  const { t } = useTranslation('settings');
  const queryClient = useQueryClient();
  const { activeOrganization } = useOrganization();
  const orgId = activeOrganization?.id ?? '';

  const SCOPE_GROUPS = getScopeGroups(t);

  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeyResponseDto | null>(null);
  const [newKeyName, setNewKeyName] = useState('');
  // Nothing is pre-selected: `mcp` used to be, which made full agent access the
  // path of least resistance for every key.
  const [newKeyScopes, setNewKeyScopes] = useState<ApiKeyScope[]>([]);
  const [createdKey, setCreatedKey] = useState<{ rawKey: string; scopes: string[] } | null>(null);
  const [copied, setCopied] = useState(false);
  const [client, setClient] = useState<McpClientId>('claude-code');

  const { data, isLoading } = useMcpApiKeysControllerListV1(
    { organizationId: orgId },
    { query: { enabled: !!orgId } },
  );

  const invalidateKeys = () => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0];
        return typeof key === 'string' && key.includes('/api-keys');
      },
    });
  };

  const createMutation = useMcpApiKeysControllerCreateV1({
    mutation: {
      onSuccess: (result) => {
        setCreatedKey({ rawKey: result.rawKey, scopes: result.apiKey.scopes });
        setCreateOpen(false);
        setNewKeyName('');
        setNewKeyScopes([]);
        invalidateKeys();
      },
    },
  });

  const revokeMutation = useMcpApiKeysControllerRevokeV1({
    mutation: {
      onSuccess: () => {
        setRevokeTarget(null);
        invalidateKeys();
      },
    },
  });

  const handleCreate = () => {
    if (!newKeyName.trim() || !orgId || newKeyScopes.length === 0) return;
    createMutation.mutate({
      data: { name: newKeyName.trim(), scopes: newKeyScopes, organizationId: orgId },
    });
  };

  const toggleScope = (scope: ApiKeyScope) => {
    setNewKeyScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );
  };

  const handleCopy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const keys = data?.keys ?? [];
  const showMcpSetup = useMemo(
    () => (createdKey ? createdKey.scopes.includes('mcp') : keys.some((k) => k.scopes.includes('mcp'))),
    [createdKey, keys],
  );

  const formatDate = (date: string | Date) => new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const primaryScope = (scopes: string[]): string => {
    // Only drives the row's avatar icon, not what the key can do. mcp and figma
    // get their own; every REST scope shares the generic API icon, so this must
    // not return a scope like 'brands:read' -- those carry no icon.
    if (scopes.includes('mcp')) return 'mcp';
    if (scopes.includes('figma')) return 'figma';
    return 'api';
  };

  // Streamable HTTP endpoint (SSE is deprecated). Built from the configured API
  // host so the snippets point at the real environment, not localhost.
  const mcpHttpUrl = `${API_URL}/api/mcp/mcp`;
  const MCP_CLIENTS: ReadonlyArray<{
    id: McpClientId;
    label: string;
    blurb?: React.ReactNode;
    steps?: string[];
    code: string;
    note: string;
  }> = [
    {
      id: 'claude-code',
      label: 'Claude Code',
      blurb: (
        <>
          Run this in your terminal (it writes the entry to <code>.mcp.json</code>):
        </>
      ),
      code: `claude mcp add --transport http nexmail ${mcpHttpUrl} \\\n  --header "Authorization: Bearer YOUR_API_KEY"`,
      note: 'Streamable HTTP with a static bearer key. No plan tier required.',
    },
    {
      id: 'claude-desktop',
      label: 'Claude Desktop',
      blurb: (
        <>
          Add to <code>claude_desktop_config.json</code>, then restart the app:
        </>
      ),
      code: JSON.stringify(
        {
          mcpServers: {
            nexmail: {
              command: 'npx',
              args: ['-y', 'mcp-remote', mcpHttpUrl, '--header', 'Authorization: Bearer YOUR_API_KEY'],
            },
          },
        },
        null,
        2,
      ),
      note: 'Bridged via mcp-remote for header auth. Pro/Max/Team can instead add it under Settings > Connectors using OAuth.',
    },
    {
      id: 'codex',
      label: 'Codex',
      blurb: (
        <>
          Add to <code>~/.codex/config.toml</code>:
        </>
      ),
      code: `[mcp_servers.nexmail]\nurl = "${mcpHttpUrl}"\nhttp_headers = { "Authorization" = "Bearer YOUR_API_KEY" }`,
      note: 'Native Streamable HTTP with a static bearer key.',
    },
    {
      id: 'chatgpt',
      label: 'ChatGPT',
      steps: [
        'Requires ChatGPT Plus, Pro, Business, Enterprise, or Edu (not Free); on workspaces an admin enables developer mode first.',
        'Open Settings > Apps > Advanced settings and turn on Developer mode.',
        'Add a custom connector and paste the server URL below.',
        'Approve each write action when prompted (required while in beta).',
      ],
      code: mcpHttpUrl,
      note: 'Configured in the ChatGPT UI (no config file). OAuth is preferred; a static API token is also accepted.',
    },
  ];
  const activeClient = MCP_CLIENTS.find((c) => c.id === client) ?? MCP_CLIENTS[0];

  return (
    <Box>
      <OrgSectionPicker />
      {/* Header */}
      <Box sx={{ mt: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              {t('apiKeys.title', 'MCP/API Keys')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('apiKeys.subtitle', 'One key can grant access to one or many capabilities -- pick the scopes it needs.')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            {t('apiKeys.newKey', 'New Key')}
          </Button>
        </Box>
      </Box>

      {/* Newly created key banner */}
      {createdKey && (
        <Alert
          severity="success"
          variant="outlined"
          sx={{
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-message': { width: '100%' },
          }}
          onClose={() => setCreatedKey(null)}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t('apiKeys.banner.created', 'Your new API key has been created')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('apiKeys.banner.copyNow', "Copy this key now. For security, it won't be displayed again.")}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                wordBreak: 'break-all',
                flex: 1,
              }}
            >
              {createdKey.rawKey}
            </Typography>
            <Tooltip title={copied ? t('apiKeys.copied', 'Copied!') : t('apiKeys.copy', 'Copy')}>
              <IconButton size="small" onClick={() => handleCopy(createdKey.rawKey)}>
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Alert>
      )}

      {/* Keys list */}
      <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
        <CardContent sx={{ p: 0 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : keys.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <KeyIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1.5 }} />
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {t('apiKeys.empty.title', 'No API keys yet')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(
                  'apiKeys.empty.body',
                  'Create a key to connect MCP clients like Claude Code, or to integrate with the nexmail REST API.',
                )}
              </Typography>
            </Box>
          ) : (
            <Stack divider={<Divider />}>
              {keys.map((key) => {
                const lead = primaryScope(key.scopes);
                const leadMeta = SCOPE_META[lead] ?? SCOPE_META.api;
                return (
                  <Box
                    key={key.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 3,
                      py: 2,
                      '&:hover': { bgcolor: 'action.hover' },
                      transition: 'background-color 0.15s',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: leadMeta.tone === 'primary' ? 'primary.50' : 'grey.100',
                          color: leadMeta.tone === 'primary' ? 'primary.main' : 'text.secondary',
                          flexShrink: 0,
                        }}
                      >
                        {leadMeta.icon}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2" noWrap>
                            {key.name}
                          </Typography>
                          {key.scopes.map((scope) => {
                            const meta = SCOPE_META[scope] ?? { label: scope.toUpperCase(), tone: 'default' as const };
                            return (
                              <Chip
                                key={scope}
                                label={meta.label}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  letterSpacing: '0.03em',
                                }}
                                color={meta.tone}
                                variant="outlined"
                              />
                            );
                          })}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          <Box component="span" sx={{ fontFamily: 'monospace' }}>
                            {key.keyPrefix}...
                          </Box>
                          {' -- '}
                          {t('apiKeys.keyRow.created', 'Created {{date}}', { date: formatDate(key.createdAt) })}
                          {key.lastUsedAt && (
                            <>{' -- '}{t('apiKeys.keyRow.lastUsed', 'Last used {{date}}', { date: formatDate(key.lastUsedAt) })}</>
                          )}
                        </Typography>
                      </Box>
                    </Box>
                    <Tooltip title={t('apiKeys.revokeKey', 'Revoke key')}>
                      <IconButton
                        size="small"
                        onClick={() => setRevokeTarget(key)}
                        sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              })}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* MCP setup instructions -- only when there's an MCP-scoped key to wire up */}
      {showMcpSetup && (
        <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'grey.50' }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <TerminalIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography variant="subtitle2">
                {t('apiKeys.mcpSetup.title', 'Connect a client to your MCP')}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
              {MCP_CLIENTS.map((c) => (
                <Chip
                  key={c.id}
                  label={c.label}
                  onClick={() => setClient(c.id)}
                  color={client === c.id ? 'primary' : 'default'}
                  variant={client === c.id ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600 }}
                />
              ))}
            </Stack>

            {activeClient.steps ? (
              <Box component="ol" sx={{ pl: 2.5, m: 0, mb: 2 }}>
                {activeClient.steps.map((step, i) => (
                  <Typography
                    key={i}
                    component="li"
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.75 }}
                  >
                    {step}
                  </Typography>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {activeClient.blurb}
              </Typography>
            )}

            <Box
              sx={{
                position: 'relative',
                p: 2.5,
                borderRadius: 1.5,
                bgcolor: '#1e1e2e',
                color: '#cdd6f4',
                fontFamily: 'monospace',
                fontSize: '0.8rem',
                lineHeight: 1.7,
                overflow: 'auto',
                whiteSpace: 'pre',
              }}
            >
              <Tooltip title={t('apiKeys.copy', 'Copy')}>
                <IconButton
                  size="small"
                  onClick={() => handleCopy(activeClient.code)}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    color: '#6c7086',
                    '&:hover': { color: '#cdd6f4' },
                  }}
                >
                  <CopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              {activeClient.code}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              {activeClient.note}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              Replace YOUR_API_KEY with a key from above. Claude Desktop and ChatGPT connect from the vendor&apos;s cloud, so the server must be reachable at a public URL (not localhost).
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Webhooks: the other half of wiring up an outside system. A key is how
          it calls us; a webhook is how we call it back. */}
      <WebhooksSection />

      {/* Create dialog */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>{t('apiKeys.createDialog.title', 'Create API Key')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            {t('apiKeys.createDialog.body', 'Give your key a descriptive name and pick which capabilities it should grant.')}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label={t('apiKeys.createDialog.keyNameLabel', 'Key Name')}
            placeholder={t('apiKeys.createDialog.keyNamePlaceholder', 'e.g. Claude Code, My Integration')}
            value={newKeyName}
            onChange={(e) => setNewKeyName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newKeyName.trim() && newKeyScopes.length > 0) handleCreate();
            }}
            sx={{ mb: 2.5 }}
          />
          {/* Agent access is a separate authority axis from the REST scopes
              below, not a finer-grained one. A key with this enabled reaches
              every MCP tool regardless of which REST scopes it carries, so it
              is presented on its own and starts off. */}
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.02em', display: 'block', mb: 1 }}
          >
            {t('apiKeys.createDialog.agentLabel', 'AGENT ACCESS')}
          </Typography>
          {(() => {
            const agentSelected = newKeyScopes.includes(CreateApiKeyDtoScopesItem.mcp);
            return (
              <Box
                role="checkbox"
                aria-checked={agentSelected}
                tabIndex={0}
                onClick={() => toggleScope(CreateApiKeyDtoScopesItem.mcp)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleScope(CreateApiKeyDtoScopesItem.mcp);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: agentSelected ? 'warning.main' : 'grey.200',
                  bgcolor: agentSelected ? 'warning.50' : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: agentSelected ? 'warning.main' : 'grey.100',
                    color: agentSelected ? 'warning.contrastText' : 'text.secondary',
                    flexShrink: 0,
                  }}
                >
                  <TerminalIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t('apiKeys.scopes.mcp.title', 'Agents & MCP clients')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t(
                      'apiKeys.scopes.mcp.description',
                      'Claude Code, Codex, OpenClaw, and other MCP clients',
                    )}
                  </Typography>
                </Box>
                <Chip
                  label={
                    agentSelected
                      ? t('apiKeys.scopes.enabled', 'Enabled')
                      : t('apiKeys.scopes.enable', 'Enable')
                  }
                  size="small"
                  color={agentSelected ? 'warning' : 'default'}
                  variant={agentSelected ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600, cursor: 'pointer', flexShrink: 0 }}
                />
              </Box>
            );
          })()}
          {newKeyScopes.includes(CreateApiKeyDtoScopesItem.mcp) && (
            <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
              <Typography variant="caption">
                {t(
                  'apiKeys.createDialog.agentWarning',
                  'A connected agent acts with the full authority of your organization: it can read and author across every brand on this org, and the REST scopes below do not limit it. Grant this only to clients you run yourself.',
                )}
              </Typography>
            </Alert>
          )}
          <Stack
            direction="row"
            sx={{ mt: 2.5, mb: 1, alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}
          >
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.02em' }}
            >
              {t('apiKeys.createDialog.scopesLabel', 'REST API SCOPES')}
            </Typography>
            <Button
              size="small"
              href={`${API_URL}/public/docs`}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              sx={{ textTransform: 'none', minWidth: 0, py: 0 }}
            >
              {t('apiKeys.createDialog.apiDocs', 'API docs')}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {t(
              'apiKeys.createDialog.scopesHelp',
              'These gate the public REST API only. Grant just what this integration needs -- scopes cannot be changed after the key is created.',
            )}
          </Typography>
          <Stack spacing={1}>
            {SCOPE_GROUPS.map((group) => {
              const anySelected = group.choices.some((c) => newKeyScopes.includes(c.value));
              return (
                <Box
                  key={group.key}
                  sx={{
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: anySelected ? 'primary.main' : 'grey.200',
                    bgcolor: anySelected ? 'primary.50' : 'transparent',
                    transition: 'all 0.15s',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: anySelected ? 'primary.main' : 'grey.100',
                        color: anySelected ? 'primary.contrastText' : 'text.secondary',
                        flexShrink: 0,
                      }}
                    >
                      {group.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {group.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {group.description}
                      </Typography>
                    </Box>
                  </Box>
                  <Stack direction="row" spacing={0.75} sx={{ mt: 1, pl: 5.5, flexWrap: 'wrap' }}>
                    {group.choices.map((choice) => {
                      const selected = newKeyScopes.includes(choice.value);
                      return (
                        <Chip
                          key={choice.value}
                          label={choice.label}
                          size="small"
                          role="checkbox"
                          aria-checked={selected}
                          onClick={() => toggleScope(choice.value)}
                          color={selected ? (choice.danger ? 'warning' : 'primary') : 'default'}
                          variant={selected ? 'filled' : 'outlined'}
                          sx={{ fontWeight: 600, cursor: 'pointer' }}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
          {/* esp:send is the only scope that can put mail in front of real
              recipients, so selecting it gets an explicit callout rather than
              being just another chip. */}
          {newKeyScopes.includes(CreateApiKeyDtoScopesItem['esp:send']) && (
            <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
              <Typography variant="caption">
                {t(
                  'apiKeys.createDialog.sendWarning',
                  'This key will be able to send campaigns to your real audiences. Anyone holding it can email your list.',
                )}
              </Typography>
            </Alert>
          )}
          {newKeyScopes.length === 0 && (
            <FormHelperText error sx={{ mt: 1 }}>
              {t('apiKeys.createDialog.scopeRequired', 'Pick at least one scope.')}
            </FormHelperText>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCreateOpen(false)} sx={{ textTransform: 'none' }}>
            {t('common:cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!newKeyName.trim() || newKeyScopes.length === 0 || createMutation.isPending}
            sx={{ textTransform: 'none', borderRadius: 2, minWidth: 100 }}
          >
            {createMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              t('apiKeys.createDialog.createKey', 'Create Key')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke confirmation */}
      <Dialog
        open={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>{t('apiKeys.revokeDialog.title', 'Revoke API Key')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t('apiKeys.revokeDialog.confirmPrefix', 'Are you sure you want to revoke')}{' '}
            <strong>{revokeTarget?.name}</strong> (
            <Box component="code" sx={{ fontSize: '0.8rem' }}>
              {revokeTarget?.keyPrefix}...
            </Box>
            )? {t('apiKeys.revokeDialog.confirmSuffix', 'Any clients using this key will immediately lose access.')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setRevokeTarget(null)} sx={{ textTransform: 'none' }}>
            {t('common:cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (revokeTarget) {
                revokeMutation.mutate({
                  keyId: revokeTarget.id,
                  params: { organizationId: orgId },
                });
              }
            }}
            disabled={revokeMutation.isPending}
            sx={{ textTransform: 'none', borderRadius: 2, minWidth: 100 }}
          >
            {revokeMutation.isPending ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              t('apiKeys.revokeDialog.revokeKey', 'Revoke Key')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
